// 📄 pages/Proyectos/MisProyectos.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { porcentajeAvance } from '../../services/hitoService.ts';
import { Link } from 'react-router-dom';
import { FolderKanban, Plus, RefreshCw, Building2, LogIn, ArchiveRestore, Eye, Globe2, User as UserIcon, Users as UsersIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useUserProjects, UserProjectInfo } from '../../context/ProjectsContext.tsx';
import { setProjectArchived, listProjectsForOrg, Project } from '../../services/projectService.ts';
import { getCliente } from '../../services/clienteService.ts';
import { plantillaDe } from './modulosConfig.ts';

const ROLE_LABELS: Record<string, string> = {
  PM: 'PM',
  tecnico: 'Consultor Técnico',
  funcional: 'Consultor Funcional',
  soporte: 'Soporte',
  cliente: 'Cliente'
};

// Ajuste pedido por el usuario (probando con la cuenta de Adrian, miembro
// invitado a "Equipo Yeison"): la grilla plana no dejaba claro cuál
// proyecto había creado él, cuál era de un compañero de la misma org, y
// cuál era un guest puro en otra org. Se agrupa en secciones usando los
// mismos flags que ya se calculaban por tarjeta (esCreador/esMiembro/
// esGuest) — cero datos nuevos, solo reordenar el render.
interface ProjectoConFlags extends Project {
  miRol: any;
  esMiembro: boolean;
  esGuest: boolean;
}

export default function MisProyectos() {
  const { user, esClienteExterno, organizations, orgActual } = useAuth();
  const { proyectos: misProyectos, loading: loadingMisProyectos } = useUserProjects();
  const [clienteNombresFallback, setClienteNombresFallback] = useState<Record<string, string>>({});
  const [showArchived, setShowArchived] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null);

  // "Resto de la plataforma" (visibilidad global de TODOS los proyectos de
  // la plataforma para el Admin) se eliminó 2026-08-20 a pedido del
  // usuario — quedó redundante desde que existe la vista de Soporte
  // dedicada (SoporteHome.tsx), que ya cubre ese caso de uso mejor
  // (buscador por usuario/org/proyecto). Acá solo queda la visibilidad
  // dentro de la organización activa (owner/admin de ESA org ven los
  // proyectos donde no son miembro directo, ver proyectosDeOrgActiva más
  // abajo) — eso sí se mantiene, es un caso distinto (org propia, no toda
  // la plataforma).
  //
  // Sub-fase G2: proyectos de la org ACTIVA, para que el owner/admin de esa
  // org vea también los que no tiene como miembro directo (ver
  // esOwnerOAdminDeOrg en firestore.rules / Sub-fase E). Bug real
  // encontrado 2026-08-20 probando con una cuenta "Miembro" (ni owner ni
  // admin de la org): el comentario original de acá asumía que si no
  // calificás la query "simplemente devuelve vacío" — pero Firestore NO
  // funciona así, una query bloqueada por las rules siempre tira
  // permission-denied (se ve en la consola del browser), nunca vacío en
  // silencio. Como la sección que consume esto YA estaba gateada a
  // owner/admin en el render (más abajo), la fetch ni hacía falta para el
  // resto — ahora el useEffect respeta la MISMA condición, evitando el
  // request de más y el error de consola para cualquier "Miembro" normal.
  const puedeVerProyectosDeOrg = orgActual?.role === 'owner' || orgActual?.role === 'admin';
  const [proyectosDeOrgActiva, setProyectosDeOrgActiva] = useState<Project[]>([]);
  const [loadingOrgActiva, setLoadingOrgActiva] = useState(true);

  useEffect(() => {
    if (!orgActual || !puedeVerProyectosDeOrg) { setProyectosDeOrgActiva([]); setLoadingOrgActiva(false); return; }
    setLoadingOrgActiva(true);
    listProjectsForOrg(orgActual.id)
      .then(setProyectosDeOrgActiva)
      .catch((err) => { console.error('Error cargando proyectos de la organización activa:', err); setProyectosDeOrgActiva([]); })
      .finally(() => setLoadingOrgActiva(false));
  }, [orgActual, puedeVerProyectosDeOrg]);

  // Fallback SOLO para proyectos sin `clienteNombre` desnormalizado
  // (legado pre-G2, ver scripts/backfill-cliente-nombre-projects.mjs). Ya
  // NO se puede hacer un listClientes() sin filtro (firestore.rules exige
  // ser miembro de la org del cliente) — se resuelve puntual, por
  // proyecto, y si falla (p. ej. cliente de otra org) se cae a "Cliente
  // desconocido" sin romper la página.
  useEffect(() => {
    const candidatos = [...misProyectos, ...proyectosDeOrgActiva]
      .filter(p => !p.clienteNombre && p.clienteId && !(p.clienteId in clienteNombresFallback));
    const idsUnicos = Array.from(new Set(candidatos.map(p => p.clienteId)));
    if (idsUnicos.length === 0) return;

    Promise.all(idsUnicos.map((clienteId) =>
      getCliente(clienteId)
        .then((c) => [clienteId, c?.name] as const)
        .catch(() => [clienteId, undefined] as const)
    )).then((pares) => {
      setClienteNombresFallback((prev) => {
        const next = { ...prev };
        pares.forEach(([id, nombre]) => { if (nombre) next[id] = nombre; });
        return next;
      });
    });
  }, [misProyectos, proyectosDeOrgActiva, clienteNombresFallback]);

  const clienteNombreDe = (p: Project) => (p.clienteId ? p.clienteNombre || clienteNombresFallback[p.clienteId] || 'Cliente desconocido' : 'Proyecto interno');

  const loading = loadingMisProyectos || loadingOrgActiva;

  // Sub-fase G2: "guest" = miembro real de un proyecto que pertenece a una
  // org de la que el usuario NO es miembro (ni owner) — se resuelve sin
  // lecturas extra cruzando contra `organizations` (ya cargado por
  // AuthContext). Los guest se muestran SIEMPRE, sin importar orgActual;
  // el resto de "mis proyectos" se filtra por orgId == orgActual.
  const esGuest = (p: UserProjectInfo) => !organizations.some(o => o.id === p.orgId);

  const projects = useMemo(() => {
    const misProyectosConFlags = misProyectos.map((p) => ({ ...p, esMiembro: true, esGuest: esGuest(p) }));
    const enOrgActivaOGuest = misProyectosConFlags.filter((p) => p.esGuest || p.orgId === orgActual?.id);

    const idsMiembro = new Set(misProyectos.map((p) => p.id));
    // Solo-vista-org: proyectos de la org activa donde el usuario ve por
    // ser owner/admin de esa org, sin ser miembro directo (nunca son
    // "guest" — son de la org activa por definición).
    const soloVistaOrg = proyectosDeOrgActiva
      .filter((p) => !idsMiembro.has(p.id))
      .map((p) => ({ ...p, miRol: null, esMiembro: false, esGuest: false }));

    return [...enOrgActivaOGuest, ...soloVistaOrg];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misProyectos, proyectosDeOrgActiva, organizations, orgActual]);

  const visibleProjects = projects.filter(p => showArchived || !p.archivado);
  const archivedCount = projects.filter(p => p.archivado).length;

  const creadosPorMi = visibleProjects.filter((p) => !!user && p.creadoPor === user.uid);
  const deLaOrgActiva = visibleProjects.filter((p) =>
    !(!!user && p.creadoPor === user.uid) && !p.esGuest && p.orgId === orgActual?.id
  );
  const comoGuest = visibleProjects.filter((p) => p.esGuest);

  const handleDesarchivar = async (projectId: string) => {
    setUnarchivingId(projectId);
    try {
      await setProjectArchived(projectId, false);
    } catch (err) {
      console.error('Error al desarchivar el proyecto:', err);
    } finally {
      setUnarchivingId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto text-white">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-light">Mis Proyectos</h1>
          <p className="text-sm text-gray-400 mt-1">Organizados por dónde participas: los que creaste, los de tu equipo y aquellos a los que te invitaron.</p>
        </div>
        {!esClienteExterno && (orgActual?.role === 'owner' || orgActual?.role === 'admin') && (
          <Link
            to="/proyectos/nuevo"
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> Nuevo Proyecto
          </Link>
        )}
      </div>

      {archivedCount > 0 && (
        <label className="flex items-center gap-2 mb-4 text-sm text-gray-400 cursor-pointer w-fit">
          <input
            type="checkbox"
            className="accent-blue-500 w-4 h-4"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Mostrar archivados ({archivedCount})
        </label>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando proyectos...</p>
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FolderKanban className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {projects.length === 0 ? 'Todavía no eres miembro de ningún proyecto.' : 'No hay proyectos activos.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          <SeccionProyectos
            titulo="Creados por mí"
            icono={UserIcon}
            proyectos={creadosPorMi}
            clienteNombreDe={clienteNombreDe}
            unarchivingId={unarchivingId}
            onDesarchivar={handleDesarchivar}
            esCreadorDe={(p) => !!user && p.creadoPor === user.uid}
          />
          <SeccionProyectos
            titulo={`Proyectos de ${orgActual?.name || 'tu organización'}`}
            subtitulo="De tu equipo, aunque no los hayas creado vos"
            icono={UsersIcon}
            proyectos={deLaOrgActiva}
            clienteNombreDe={clienteNombreDe}
            unarchivingId={unarchivingId}
            onDesarchivar={handleDesarchivar}
            esCreadorDe={(p) => !!user && p.creadoPor === user.uid}
          />
          <SeccionProyectos
            titulo="Invitado en otras organizaciones"
            subtitulo="Sos miembro directo de estos proyectos, pero no de la organización que los aloja"
            icono={Globe2}
            proyectos={comoGuest}
            clienteNombreDe={clienteNombreDe}
            unarchivingId={unarchivingId}
            onDesarchivar={handleDesarchivar}
            esCreadorDe={(p) => !!user && p.creadoPor === user.uid}
          />
        </div>
      )}
    </div>
  );
}

interface SeccionProyectosProps {
  titulo: string;
  subtitulo?: string;
  icono: React.ComponentType<{ className?: string }>;
  proyectos: ProjectoConFlags[];
  clienteNombreDe: (p: Project) => string;
  unarchivingId: string | null;
  onDesarchivar: (projectId: string) => void;
  esCreadorDe: (p: Project) => boolean;
}

// Una sección vacía simplemente no se muestra — evita encabezados
// "Invitado en otras organizaciones" seguidos de nada cuando el usuario
// no tiene ningún guest, por ejemplo.
function SeccionProyectos({ titulo, subtitulo, icono: Icono, proyectos, clienteNombreDe, unarchivingId, onDesarchivar, esCreadorDe }: SeccionProyectosProps) {
  if (proyectos.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Icono className="w-4 h-4 text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">{titulo}</h2>
        <span className="text-xs text-gray-500">({proyectos.length})</span>
      </div>
      {subtitulo && <p className="text-xs text-gray-500 mb-3">{subtitulo}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
        {proyectos.map((p) => (
          <TarjetaProyecto
            key={p.id}
            p={p}
            clienteNombre={clienteNombreDe(p)}
            esCreador={esCreadorDe(p)}
            unarchivingId={unarchivingId}
            onDesarchivar={onDesarchivar}
          />
        ))}
      </div>
    </div>
  );
}

interface TarjetaProyectoProps {
  p: ProjectoConFlags;
  clienteNombre: string;
  esCreador: boolean;
  unarchivingId: string | null;
  onDesarchivar: (projectId: string) => void;
}

// Color por tipo de proyecto (T0 de plan-tickets-proyecto.md).
const TIPO_CHIP: Record<string, string> = {
  general: 'bg-white/5 text-gray-300 border-white/15',
  software: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  'sap-ariba': 'bg-amber-500/10 text-amber-300 border-amber-500/20'
};

function TarjetaProyecto({ p, clienteNombre, esCreador, unarchivingId, onDesarchivar }: TarjetaProyectoProps) {
  const tipo = plantillaDe(p);
  return (
    <div
      className={`bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 transition-all ${
        p.archivado ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <Link to={`/proyecto/${p.id}/detalle`} className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold hover:text-blue-400 transition-colors truncate">
              {p.nombre}
            </h3>
            {p.archivado && (
              <span className="flex-shrink-0 text-[10px] bg-gray-700 text-gray-300 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                Archivado
              </span>
            )}
          </div>
        </Link>
        <Link
          to={`/proyecto/${p.id}`}
          title="Entrar al proyecto"
          className="flex-shrink-0 flex items-center gap-1.5 text-xs bg-white/5 hover:bg-blue-500 border border-white/10 hover:border-blue-500 px-2.5 py-1 rounded-lg transition-all"
        >
          <LogIn className="w-3.5 h-3.5" /> Entrar
        </Link>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <p className="text-sm text-gray-400 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> {clienteNombre}
        </p>
        <span
          title={tipo.descripcion}
          className={`text-[10px] px-2 py-0.5 rounded-full border ${TIPO_CHIP[tipo.id] || TIPO_CHIP.general}`}
        >
          {tipo.icon} {tipo.label}
        </span>
      </div>
      {p.avanceHitos && p.avanceHitos.total > 0 && (
        <div className="mb-3" title={`${p.avanceHitos.completados} de ${p.avanceHitos.total} hitos completados`}>
          <div className="flex justify-between text-[11px] text-gray-500 mb-1">
            <span>Avance</span>
            <span>{porcentajeAvance(p.avanceHitos)}%</span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${porcentajeAvance(p.avanceHitos)}%` }} />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {p.miembros.length} miembro{p.miembros.length !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          {p.miRol && (
            <span className="text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full">
              {ROLE_LABELS[p.miRol] || p.miRol}
            </span>
          )}
          {p.esGuest && (
            <span
              title="Sos miembro directo de este proyecto, pero no de la organización que lo aloja"
              className="flex items-center gap-1 text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-2 py-0.5 rounded-full"
            >
              <Globe2 className="w-3 h-3" /> Guest
            </span>
          )}
          {!p.esMiembro && !p.esGuest && (
            <span
              title="Estás viendo este proyecto sin ser miembro directo (owner/admin de la organización, o admin de plataforma)"
              className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full"
            >
              <Eye className="w-3 h-3" /> Vista Admin
            </span>
          )}
          {p.archivado && esCreador && (
            <button
              onClick={() => onDesarchivar(p.id)}
              disabled={unarchivingId === p.id}
              className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 disabled:opacity-50 transition-all"
            >
              <ArchiveRestore className="w-3 h-3" />
              {unarchivingId === p.id ? 'Desarchivando...' : 'Desarchivar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
